// Importing React and external libs
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Importing React DND
import { useDrag, useDrop } from 'react-dnd';

// Importing antd icons
import { HolderOutlined } from '@ant-design/icons';

// Importing Helix modules
import 'helix-modules/sNumber';

// Import component model
import { DND_ITEM_TYPE_WELL } from './model/well';

// Importing assets
import './style.scss';

// Defining constants
export const DEFAULT_CFG = {
  plate: {
    cols: 12,
    colsLabel: [],
    padding: 5,
    rows: 8,
    rowsLabel: [],
  },
  well: {
    margin: 5,
    radius: 10,
  }
};

export const WELLS_COLOR = {
  available: '#fff',
  unavailable: '#9fcdf0',
  used: '#004a9d',
};
const WELL_HOVER_COLOR = '#d9ebf9';

export const getCoord = ({ x, y }) => `${y.toAlphabet()}${x < 9 ? '0' : ''}${x + 1}`;
export const getPosition = (coord) => ({
  x: Number(coord.replace(/[a-zA-Z]+/, '')) - 1,
  y: Number(0).fromAlphabet(coord.replace(/\d+/, '')),
});

export const getEmptyMatrix = ({ cols, rows, state = 'available', wellsColor }) => {
  wellsColor = wellsColor || WELLS_COLOR;
  const matrix = [];
  cols = cols || DEFAULT_CFG.plate.cols;
  rows = rows || DEFAULT_CFG.plate.rows;
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      matrix.push({
        color: wellsColor[state],
        label: `${getCoord({ x, y })} - ${state}`,
        state,
        type: DND_ITEM_TYPE_WELL,
        x,
        y,
      });
    }
  }
  return matrix;
};

export const Source = ({ item, disabled, title, onSelect }) => {
  const [{ dragging }, drag] = useDrag({
    canDrag: !disabled,
    item,
    type: DND_ITEM_TYPE_WELL,
    collect: (monitor) => ({
      dragging: monitor.isDragging() ? 'dragging' : '',
    }),
  });
  return (
    <div
      className={`draggable-source ${disabled && 'disabled'}`}
      onClick={() => !disabled && onSelect && onSelect(item)}
    >
      <span className={dragging} title={item?.name} ref={drag}>
        <HolderOutlined />&nbsp;&nbsp;&nbsp;{title || item?.name}
      </span>
    </div>
  );
};

const Well = (props) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: DND_ITEM_TYPE_WELL,
    drop: props.onDrop,
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
  });
  const oProps = {
    ...props,
    fill: canDrop && isOver ? WELL_HOVER_COLOR : props.fill,
  };
  const { title } = props;
  return (
    <circle className="well" ref={drop} {...oProps}>
      {title && (<title>{title}</title>)}
    </circle>
  );
};

const Plate = (props) => {
  const {
    configuration,
    data: propData,
    height,
    id,
    showLabel,
    width,
  } = props;
  const [data, setData] = useState();

  /**
   * Helper to calculate the viewport size
   * @param {void}
   * @return {object}
   * labelSize {number}
   * vHeight {number}
   * vWidth {number}
   * wellSize {number}
   **/
  const _getViewport = () => {
    const { plate, well } = {
      ...DEFAULT_CFG,
      ...configuration,
    };
    const wellSize = well.margin + (well.radius * 2);
    const labelSize = showLabel ? wellSize : 0;
    return {
      labelSize,
      vHeight: (wellSize * plate.rows) + (plate.padding * 2) + labelSize + 10,
      vWidth: (wellSize * plate.cols) + (plate.padding * 2) + labelSize + 10,
      wellSize,
    };
  };

  /**
   * Helper to draw the labels
   * @param {number} labelSize
   * @return {object} react component
   **/
  const _label = (labelSize) => {
    const { plate, well } = {
      ...DEFAULT_CFG,
      ...configuration,
    };
    const colsLabel = [...Array.apply(null, { length: plate.cols })].map((i, j) => {
      const fixMiddleChar = (j < 9 ? 3 : 6);
      const x = labelSize + (well.margin * j) + ((j + 1) * well.radius * 2) - fixMiddleChar;
      return <text className="label-col" key={j} x={x} y={labelSize / 2}>{j + 1}</text>;
    });

    const rowsLabel = [...Array.apply(null, { length: plate.rows })].map((i, j) => {
      const fixMiddleChar = 3;
      const y = labelSize + (well.margin * j) + ((j + 1) * well.radius * 2) + fixMiddleChar;
      return <text className="label-row" key={j} x={labelSize / 2} y={y}>{j.toAlphabet()}</text>;
    });

    return (
      <g>
        <g>{colsLabel}</g>
        <g>{rowsLabel}</g>
      </g>
    );
  };

  /**
   * Helper to draw the plate
   * @param {number} labelSize
   * @param {number} vHeight
   * @param {number} vWidth
   * @return {object} react component
   **/
  const _plate = (labelSize, vHeight, vWidth) => {
    const { plate, well: cwell } = {
      ...DEFAULT_CFG,
      ...configuration,
    };
    const height = vHeight - labelSize;
    const width = vWidth - labelSize;

    for (let y = 0; y < plate.rows; y++) {
      for (let x = 0; x < plate.cols; x++) {
        const well = data.find((well) => well.y === y && well.x === x);
        if (well) {
          well.cx = labelSize + (cwell.margin * x) + ((x + 1) * cwell.radius * 2);
          well.cy = labelSize + (cwell.margin * y) + ((y + 1) * cwell.radius * 2);
          well.r = cwell.radius;
        }
      }
    }

    const notchOffset = labelSize;
    const notchSize = 15;
    // To draw a notch we need 3 points but only 2 values (equilateral triangle)
    // top left     -> start, start
    // bottom left  -> start, end
    // bottom right -> end,   end
    const notchStart = notchOffset;
    const notchEnd = notchOffset + notchSize - 1;

    return (
      <g>
        <rect
          className="plate"
          height={height - 1}
          width={width - 1}
          x={labelSize}
          y={labelSize}
        />
        <g>
          {data.map((well, index) => (
            <Well
              cx={well.cx}
              cy={well.cy}
              disabled={well.disabled}
              fill={well.color}
              key={index}
              r={cwell.radius}
              title={well.label}
              onClick={well.onClick}
              onDrop={well.onDrop}
            />
          ))}
        </g>
        <polygon
          className="plate-notch"
          transform={`translate(0, ${height - notchSize})`}
          points={`${notchStart} ${notchStart}, ${notchStart} ${notchEnd}, ${notchEnd}, ${notchEnd}`}
        />
      </g>
    );
  };

  useEffect(() => {
    if (propData) setData(propData);
  }, [propData]);

  const { labelSize, vHeight, vWidth, wellSize } = _getViewport();
  return (
    <div
      className="chart-well-plate"
      id={id}
      style={{ height: height + 10, padding: 5, width: width + 10 }}
    >
      <svg viewBox={`0 0 ${vWidth} ${vHeight}`}>
        {showLabel && _label(labelSize)}
        {data && _plate(labelSize, vHeight, vWidth)}
      </svg>
    </div>
  );
};

Plate.propTypes = {
  configuration: PropTypes.object,
  data: PropTypes.array,
  height: PropTypes.number,
  showLabel: PropTypes.bool,
  width: PropTypes.number,
};

Plate.defaultProps = {
  configuration: DEFAULT_CFG,
  data: [],
  height: 400,
  showLabel: true,
  width: 600,
};

export default Plate;
